import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, Typography } from '@material-ui/core'

import { IOSSwitch } from '../IOSSwitch'
import { DefaultSelect } from '../DefaultSelect'
import { MsStoresLoadingProps } from './StoreReturnsSettings'
import { MoySkladDataLoadingComponent } from '../MoySkladDataLoading'

import { useLogic } from '../../hooks/storeHook'
import { useBoolState, useToggleState } from '../../hooks/commonHooks'
import { useMessages } from '../../hooks/snackbarHooks'
import { useSettingsPageStyles } from '../../hooks/settingsPageStylesHook'

import { SetStateFunc, valuesOfNumericEnum } from '../../common/tsUtils'

import { StoreAccountProps } from '../../types/accountProps'
import { integrationTypeNames } from '../../types/integrationTypeUtils'
import { SupplyShipmentStatus } from '../../server/mpsklad_core/Entity/Enums/SupplyShipmentStatus'

export type StoreSuppliesSettingsProps =
  StoreAccountProps
  & MsStoresLoadingProps
  & {
  isAllowed: boolean
  autoMoveHint: string
  deliveryTypeHint: string

  selectedStatus: SupplyShipmentStatus
  setSelectedStatus: SetStateFunc<SupplyShipmentStatus>

  onSubmit: (formData: SuppliesFormData) => Promise<void>
}

export type SuppliesFormData = {
  autoCreateMoveDocsInMoySklad: boolean
  supplyMoveSourceStoreId: string | undefined
  supplyMoveTargetStoreId: string | undefined
}

export const supplyShipmentStatusNames: {
  readonly [status in SupplyShipmentStatus]: string
} = {
  [SupplyShipmentStatus.Created]: 'Создана',
  [SupplyShipmentStatus.InTransit]: 'В пути/Отгружена',
  [SupplyShipmentStatus.Accepted]: 'Принята полностью',
  [SupplyShipmentStatus.PartiallyAccepted]: 'Принята частично'
} as const

const statusOptions =
  valuesOfNumericEnum(SupplyShipmentStatus).map(status => ({
    value: status,
    label: supplyShipmentStatusNames[status]
  }))

export const StoreSuppliesSettings =
  observer(
    ({
       account, msStores, loadMsStores,
       isAllowed, deliveryTypeHint, autoMoveHint,
       selectedStatus, setSelectedStatus,
       onSubmit
     }: StoreSuppliesSettingsProps) => {
      const classes = useSettingsPageStyles()

      const logic = useLogic()
      const {showSuccess} = useMessages()

      const [autoCreateMoveDocsEnabled, toggleAutoCreateMoveDocsEnabled] =
        useToggleState(account.autoCreateMoveDocsInMoySklad)

      const [sourceStoreId, setSourceStoreId] = useState<string | null>(account.supplyMoveSourceStoreId ?? null)

      const [targetStoreId, setTargetStoreId] = useState<string | null>(account.supplyMoveTargetStoreId ?? null)

      const [isMsStoresLoading, setMsStoresLoading, setMsStoresLoaded] = useBoolState()
      const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()

      // Load MS stores
      useEffect(() => {
        if (!msStores && isAllowed) {
          setMsStoresLoading()
          loadMsStores().finally(setMsStoresLoaded)
        }
      }, [msStores, loadMsStores, isAllowed, setMsStoresLoading, setMsStoresLoaded])

      const msStoreOptions = msStores?.map(store => ({
        value: store.id,
        label: store.name
      })) ?? []

      const onSubmitClick =
        async () => {
          if (isSubmitting) {
            return
          }

          setSubmitting()

          try {
            await onSubmit({
              autoCreateMoveDocsInMoySklad: autoCreateMoveDocsEnabled,
              supplyMoveSourceStoreId: sourceStoreId ?? undefined,
              supplyMoveTargetStoreId: targetStoreId ?? undefined
            })

            await logic.loadUserAccounts()
            showSuccess('Сохранено!')
          } catch (e) {
            console.error(`Failed to save ${account.integrationType} supplies settings`, e)
          } finally {
            setSubmitted()
          }
        }

      if (!isAllowed) {
        return (
          <Box className={classes.pageContainer}>
            <Typography className={classes.header}>Настройки поставок ({deliveryTypeHint})</Typography>

            <Box mt={2}>
              <Typography className={classes.helperText}>
                Функция автосоздания перемещений недоступна для вашего аккаунта.
              </Typography>
            </Box>
          </Box>
        )
      }

      if (isMsStoresLoading) {
        return <MoySkladDataLoadingComponent/>
      }

      return (
        <Box className={classes.pageContainer}>
          <Typography className={classes.header}>Настройки поставок ({deliveryTypeHint})</Typography>

          <Grid container spacing={3}>
            <Grid item xs={9}>
              <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                <Grid item className={classes.root} xs={12}>
                  <Box width="100%">
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={autoCreateMoveDocsEnabled ? 2 : 0}
                    >
                      <FormLabel className={classes.unifiedPricesLabel}>
                        Автосоздание перемещений {autoMoveHint}
                      </FormLabel>

                      <IOSSwitch
                        checked={autoCreateMoveDocsEnabled}
                        onChange={toggleAutoCreateMoveDocsEnabled}
                      />
                    </Box>

                    {autoCreateMoveDocsEnabled && (
                      <Box>
                        <Box mb={1}>
                          <FormLabel component="legend" className={classes.label}>
                            <span className={classes.dash}>—</span>
                            Статус поставки для создания перемещения
                          </FormLabel>
                        </Box>
                        <DefaultSelect
                          fullWidth
                          value={selectedStatus}
                          options={statusOptions}
                          onChange={(value) => {
                            if (value !== undefined) {
                              setSelectedStatus(value)
                            }
                          }}
                        />
                        <Box mt={1}>
                          <FormHelperText>
                            При достижении выбранного статуса поставки будет автоматически создан документ перемещения в
                            МоёмСкладе
                          </FormHelperText>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Warehouse Mapping Section */}
            {autoCreateMoveDocsEnabled && (
              <Grid item xs={9}>
                <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                  <Grid item className={classes.root} xs={12}>
                    <Box mb={2}>
                      <Typography style={{fontSize: 14, fontWeight: 600, marginBottom: 8}}>
                        Сопоставление складов и параметры перемещения
                      </Typography>
                      <FormHelperText>
                        Укажите, с какого склада в МоёмСкладе будет списываться товар при создании документа
                        «Перемещение»
                        и на какой склад {integrationTypeNames[account.integrationType]} он будет поступать
                      </FormHelperText>
                    </Box>

                    <Box mb={3}>
                      <Box mb={1}>
                        <FormLabel component="legend" className={classes.label}>
                          <span className={classes.dash}>—</span>
                          Склад источник
                        </FormLabel>
                      </Box>
                      <DefaultSelect
                        fullWidth
                        withUnselectedValue
                        placeholder="Основной склад"
                        value={sourceStoreId}
                        options={msStoreOptions}
                        onChange={(value) => setSourceStoreId(value ?? null)}
                      />
                    </Box>

                    <Box>
                      <Box mb={1}>
                        <FormLabel component="legend" className={classes.label}>
                          <span className={classes.dash}>—</span>
                          Склад назначение
                        </FormLabel>
                      </Box>
                      <DefaultSelect
                        fullWidth
                        withUnselectedValue
                        placeholder="Не выбрано"
                        value={targetStoreId}
                        options={msStoreOptions}
                        onChange={(value) => setTargetStoreId(value ?? null)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Box display="flex">
            <button
              className={clsx(classes.saveButton, 'default-button')}
              disabled={isSubmitting}
              onClick={onSubmitClick}
            >
              <p>{isSubmitting ? 'Сохраняем...' : 'Сохранить'}</p>
            </button>
          </Box>
        </Box>
      )
    })